import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import { format } from 'date-fns';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DayPickerFormInput } from '../../../../../design-system/Form/DayPickerFormInput';
import {
    DeferAgreement as DeferAgreementType,
    DossierId,
} from '../../../../../resources/dossier/types';
import { useGetDossierData } from '../../../../../resources/dossier/queries';
import { useSpecifyDeferAgreementMutation } from '../../../../../resources/dossier/mutations';

import s from './DeferAgreement.module.css';

export const DeferAgreement = ({ dossierId }: { dossierId: DossierId }): JSX.Element => {
    const intl = useIntl();

    const { contractDeferAgreement: deferAgreement } = useGetDossierData(dossierId) || {};
    const specifyDeferAgreementMutation = useSpecifyDeferAgreementMutation(dossierId);

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [amount, setAmount] = useState<string>('');
    const [currency, setCurrency] = useState<string>('EUR');
    const [expireAt, setExpireAt] = useState<Date | null>(null);

    const handleModal = (open: boolean) => setIsModalOpened(open);

    const handleDayPicker = (day?: Date) => {
        if (!day) {
            return;
        }

        setExpireAt(day);
    };

    const onChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
        setAmount(e.target.value);
    };

    const onChangeCurrency = (e: ChangeEvent<HTMLSelectElement>): void => {
        setCurrency(e.target.value);
    };

    const saveDeferAgreement = () => {
        const formattedExpireAt = expireAt ? format(expireAt, 'yyyy-MM-dd') : null;

        specifyDeferAgreementMutation.mutate({ amount, currency, expireAt: formattedExpireAt });
        handleModal(false);
    };

    const showDeferAgreementSpecified = (agreement: DeferAgreementType) => {
        return (
            <div className={s.deferAgreementSpecifiedContainer}>
                <div className={s.titleContainer}>
                    <h3 className={s.title}>
                        <FormattedMessage id="dossier.defer_agreement.title" />
                    </h3>
                </div>
                <p>
                    {agreement.expireAt ? (
                        <FormattedMessage
                            id={'dossier.defer_agreement.data'}
                            values={{
                                amount: agreement.amount,
                                currency: agreement.currency,
                                expireAt: agreement.expireAt,
                            }}
                        />
                    ) : (
                        agreement.amount + agreement.currency
                    )}
                </p>
                <Button priority="secondary" onClick={() => handleModal(true)}>
                    <FormattedMessage id="dossier.defer_agreement.edit" />
                </Button>
            </div>
        );
    };

    const showSpecifyDeferAgreement = () => {
        return (
            <div className={s.specifyDeferAgreementContainer}>
                <div className={s.titleContainer}>
                    <h3 className={s.title}>
                        <FormattedMessage id="dossier.defer_agreement.title" />
                    </h3>
                </div>
                <p>
                    <FormattedMessage id="dossier.defer_agreement.information" />
                </p>
                <Button priority="secondary" onClick={() => handleModal(true)}>
                    <FormattedMessage id="dossier.defer_agreement.specify" />
                </Button>
            </div>
        );
    };

    return (
        <>
            {deferAgreement && Object.entries(deferAgreement).length > 0
                ? showDeferAgreementSpecified(deferAgreement)
                : showSpecifyDeferAgreement()}

            <Modal
                isOpen={isModalOpened}
                title={intl.formatMessage({
                    id: 'dossier.defer_agreement.title',
                })}
                mainButton={
                    <Button
                        priority="primary"
                        onClick={saveDeferAgreement}
                        data-testid="dossier-information-update-traveller-name"
                    >
                        <FormattedMessage id="form.submit" />
                    </Button>
                }
                secondaryButton={
                    <Button
                        priority="secondary"
                        onClick={() => handleModal(false)}
                        data-testid="dossier-information-cancel-traveller-name"
                    >
                        <FormattedMessage id="form.cancel" />
                    </Button>
                }
            >
                <p>
                    <FormattedMessage id={`dossier.defer_agreement.message`} />
                </p>
                <div className={s.deferAgreementForm}>
                    <p>
                        <FormattedMessage id="dossier.defer_agreement.amount.information" />
                    </p>
                    <label>
                        <FormattedMessage id="dossier.defer_agreement.amount.label" />
                    </label>
                    <div className={s.deferAgreementAmountInput}>
                        <input
                            type="number"
                            step="0.01"
                            name="amount"
                            value={amount}
                            onChange={onChangeAmount}
                        />
                        <select
                            name="currency"
                            id="currency"
                            value={currency}
                            onChange={onChangeCurrency}
                            required
                        >
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            <option value="USD">USD</option>
                            <option value="CAD">CAD</option>
                            <option value="CHF">CHF</option>
                            <option value="SEK">SEK</option>
                            <option value="DKK">DKK</option>
                        </select>
                    </div>
                    <div className={s.deferAgreementDate}>
                        <p>
                            <FormattedMessage id="dossier.defer_agreement.expire_at.information" />
                        </p>
                        <label>
                            <FormattedMessage id="dossier.defer_agreement.expire_at.label" />
                        </label>
                        <DayPickerFormInput name="day-picker" onDayChange={handleDayPicker} />
                    </div>
                </div>
                <p className={s.deferAgreementWarning}>
                    <FormattedMessage id="dossier.defer_agreement.warning" />
                </p>
            </Modal>
        </>
    );
};
